import React from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FolderOpen, Move } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

interface CsvProject {
  id: string;
  name: string;
  description?: string;
  color?: string;
}

interface FileProjectSelectorProps {
  selectedFiles: Set<string>;
  onFilesMove?: () => void;
  className?: string;
}

export const FileProjectSelector: React.FC<FileProjectSelectorProps> = ({
  selectedFiles,
  onFilesMove,
  className = ''
}) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Fetch user's projects
  const { data: projects = [] } = useQuery({
    queryKey: ['csv-projects', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from('csv_projects')
        .select('*')
        .eq('user_id', user.id)
        .order('name', { ascending: true });
        
      if (error) throw error;
      return data as CsvProject[];
    },
    enabled: !!user?.id,
  });

  // Move files to project mutation
  const moveFilesMutation = useMutation({
    mutationFn: async (projectId: string | null) => {
      if (!user?.id) throw new Error('User not authenticated');
      
      const fileIds = Array.from(selectedFiles);
      console.log('Moving files:', { fileIds, projectId, userId: user.id });

      const { data, error } = await supabase
        .from('user_csv_uploads')
        .update({ 
          project_id: projectId
        })
        .in('id', fileIds);
        
      if (error) {
        console.error('Move files error:', error);
        throw new Error(`Failed to move files: ${error.message}`);
      }
      
      console.log('Move files success:', data);
    },
    onSuccess: (_, projectId) => {
      queryClient.invalidateQueries({ queryKey: ['csv-uploads'] });
      queryClient.invalidateQueries({ queryKey: ['csv-projects'] });
      
      const projectName = projectId 
        ? projects.find(p => p.id === projectId)?.name || 'Unknown Project'
        : 'Uncategorized';
        
      toast.success(`Moved ${selectedFiles.size} file(s) to ${projectName}`);
      onFilesMove?.();
    },
    onError: (error) => {
      console.error('Error moving files:', error);
      toast.error('Failed to move files');
    },
  });

  const handleMoveFiles = (projectId: string | 'uncategorized') => {
    const targetProjectId = projectId === 'uncategorized' ? null : projectId;
    moveFilesMutation.mutate(targetProjectId);
  };

  if (selectedFiles.size === 0) {
    return null;
  }

  const getProjectColor = (color?: string) => {
    return color || '#3B82F6';
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <Badge variant="secondary" className="flex items-center gap-1">
        <Move className="h-3 w-3" />
        {selectedFiles.size} file(s) selected
      </Badge>
      
      <Select onValueChange={handleMoveFiles}>
        <SelectTrigger className="w-48">
          <SelectValue placeholder="Move to project..." />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="uncategorized">
            <div className="flex items-center gap-2">
              <FolderOpen className="h-4 w-4" />
              <span>Uncategorized</span>
            </div>
          </SelectItem>
          {projects.map((project) => (
            <SelectItem key={project.id} value={project.id}>
              <div className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: getProjectColor(project.color) }}
                />
                <span>{project.name}</span>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      
      {moveFilesMutation.isPending && (
        <span className="text-sm text-muted-foreground">Moving...</span>
      )}
    </div>
  );
};